import Papa from "papaparse";

import {
  BankFormat,
  ColumnMapping,
  TransactionField,
  buildDefaultMappings,
  detectBankFormat,
  parseAmountWithFormat,
  parseDateWithFormat,
} from "./bankFormats";
import { CsvTransactionInput } from "../../../../../api/graphql/mutations/transaction/bulkCreateCsvTransactions";

export interface ParsedRow {
  /** Original CSV row data keyed by column header */
  raw: Record<string, string>;
  /** Row index (1-based, excluding header) */
  index: number;
}

export interface CsvParseResult {
  headers: string[];
  rows: ParsedRow[];
  detectedFormat: BankFormat | null;
  mappings: ColumnMapping[];
}

/**
 * Detect the character encoding of a file buffer.
 *
 * Checks in order:
 *  1. UTF-16 LE BOM (FF FE)
 *  2. UTF-16 BE BOM (FE FF)
 *  3. UTF-8 BOM (EF BB BF)
 *  4. Heuristic: if >40% of the first 200 odd-index bytes are 0x00,
 *     the content is very likely UTF-16 LE with ASCII text (no BOM).
 *     HVB exports fall into this category.
 */
const detectEncoding = (bytes: Uint8Array): string => {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return "utf-16le";
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return "utf-16be";
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
    return "utf-8";

  // BOM-less UTF-16 LE heuristic: every second byte is 0x00 for ASCII content
  const sample = Math.min(bytes.length, 200);
  let nullOddBytes = 0;
  for (let i = 1; i < sample; i += 2) {
    if (bytes[i] === 0x00) nullOddBytes++;
  }
  if (nullOddBytes / (sample / 2) > 0.4) return "utf-16le";

  return "utf-8";
};

/**
 * Read a File as text, handling UTF-16 LE/BE encoding (e.g. HVB exports).
 * Falls back to UTF-8 for all other files. Strips the BOM if present.
 */
const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      const encoding = detectEncoding(new Uint8Array(buffer));
      const text = new TextDecoder(encoding).decode(buffer);
      // Strip BOM (U+FEFF) that may appear at the start after decoding
      resolve(text.startsWith("\uFEFF") ? text.slice(1) : text);
    };
    reader.readAsArrayBuffer(file);
  });

export const parseCsvFile = async (file: File): Promise<CsvParseResult> => {
  const text = await readFileAsText(file);
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Normalise headers: trim whitespace and drop empty/blank columns
        // (e.g. trailing semicolons in HVB/Ausgaben files create phantom "" columns)
        const headers = (results.meta.fields ?? [])
          .map((h) => h.trim())
          .filter((h) => h !== "");

        // Remap row objects to use the normalised (trimmed) header keys
        const rows: ParsedRow[] = results.data.map((row, i) => {
          const normalized: Record<string, string> = {};
          for (const [key, value] of Object.entries(row)) {
            const trimmedKey = key.trim();
            if (trimmedKey) {
              normalized[trimmedKey] = value as string;
            }
          }
          return { raw: normalized, index: i + 1 };
        });

        const detectedFormat = detectBankFormat(headers);
        const mappings = buildDefaultMappings(
          headers,
          detectedFormat,
          rows.slice(0, 20).map((r) => r.raw)
        );
        resolve({ headers, rows, detectedFormat, mappings });
      },
      error: (err: Error) => reject(err),
    });
  });
};

/**
 * Apply the user-confirmed column mappings to a set of parsed rows,
 * producing transaction inputs ready to send to the backend.
 */
export const applyMappings = (
  rows: ParsedRow[],
  mappings: ColumnMapping[],
  format: BankFormat | null
): (CsvTransactionInput & {
  _rowIndex: number;
  _hasError: boolean;
  _errorMessage?: string;
})[] => {
  const amountMapping = mappings.find((m) => m.targetField === "amount");
  const amountColumn = amountMapping?.csvColumn;
  const amountFormat = amountMapping?.amountFormat ?? "international";
  const amountSign = format?.amountSign ?? 1;
  const dateMapping = mappings.find((m) => m.targetField === "paymentDate");
  const dateColumn = dateMapping?.csvColumn;
  const dateFormat = dateMapping?.dateFormat ?? "iso";
  const nameColumn = mappings.find((m) => m.targetField === "name")?.csvColumn;
  const ibanColumn = mappings.find((m) => m.targetField === "iban")?.csvColumn;
  const descColumn = mappings.find(
    (m) => m.targetField === "description"
  )?.csvColumn;
  const currencyColumn = mappings.find(
    (m) => m.targetField === "currency"
  )?.csvColumn;

  return rows.map((row) => {
    let amount: number | undefined;
    let hasError = false;
    let errorMessage: string | undefined;

    if (amountColumn) {
      const rawAmount = row.raw[amountColumn]?.trim();
      if (rawAmount) {
        const parsed = parseAmountWithFormat(rawAmount, amountFormat);
        if (isNaN(parsed)) {
          hasError = true;
          errorMessage = `Cannot parse amount: "${rawAmount}"`;
        } else {
          amount = parsed * amountSign;
        }
      }
    }

    let paymentDate: string | undefined;
    if (dateColumn) {
      const rawDate = row.raw[dateColumn]?.trim();
      if (rawDate) {
        const iso = parseDateWithFormat(rawDate, dateFormat);
        if (iso) {
          paymentDate = iso;
        } else {
          hasError = true;
          errorMessage = errorMessage ?? `Cannot parse date: "${rawDate}"`;
        }
      }
    }

    if (!amount) {
      hasError = true;
      errorMessage = errorMessage ?? "Amount is missing or zero";
    }
    if (!paymentDate) {
      hasError = true;
      errorMessage = errorMessage ?? "Payment date is missing";
    }

    // Currency: read from mapped column. If the column is mapped but the cell is
    // blank, flag as an error rather than silently defaulting to EUR — the user
    // may have mapped the wrong column.
    const rawCurrency = currencyColumn
      ? row.raw[currencyColumn]?.trim().toUpperCase()
      : undefined;
    let currency = "EUR";
    if (currencyColumn) {
      if (!rawCurrency) {
        hasError = true;
        errorMessage =
          errorMessage ?? "Currency column is mapped but value is empty";
      } else {
        currency = rawCurrency;
      }
    }

    return {
      _rowIndex: row.index,
      _hasError: hasError,
      _errorMessage: errorMessage,
      amount: amount ?? 0,
      paymentDate: paymentDate ?? "",
      currency,
      name: nameColumn ? row.raw[nameColumn]?.trim() || undefined : undefined,
      iban: (() => {
        const raw = ibanColumn
          ? row.raw[ibanColumn]?.trim().toUpperCase()
          : undefined;
        // Strip IBANs that don't match the standard format — bank exports
        // (e.g. Qonto card payments) sometimes fill this column with internal
        // masked references like "XXXXXXXXXXXXXXXXXXXX9766555".
        const IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/;
        return raw && IBAN_REGEX.test(raw) ? raw : undefined;
      })(),
      description: descColumn
        ? row.raw[descColumn]?.trim() || undefined
        : undefined,
    };
  });
};

/** All mappable transaction fields */
export const TRANSACTION_FIELDS: TransactionField[] = [
  "amount",
  "paymentDate",
  "currency",
  "name",
  "iban",
  "description",
  "ignore",
];
